const markets = [
  {
    region: "Russia & CIS",
    flag: "🇷🇺",
    desc: "Direct FCL/LCL shipments via Vladivostok, St. Petersburg, and Novorossiysk. China-Europe Railway Express (中欧班列) available for inland delivery to Moscow and Yekaterinburg.",
    routes: ["Guangzhou → Vladivostok", "Ningbo → St. Petersburg", "Rail: Xi'an → Moscow"],
    highlight: true,
  },
  {
    region: "Middle East",
    flag: "🇦🇪",
    desc: "Regular FCL consolidations to Jebel Ali (Dubai), Dammam, and Muscat. Competitive transit times of 18–22 days from Guangzhou Port.",
    routes: ["Guangzhou → Jebel Ali", "Ningbo → Dammam", "Guangzhou → Muscat"],
    highlight: false,
  },
  {
    region: "European Markets",
    flag: "🇩🇪",
    desc: "Multimodal solutions combining sea freight and rail. Delivery to Hamburg, Rotterdam, Warsaw, and Gdańsk. EAC-certified documentation for EAEU customs clearance.",
    routes: ["Sea: Guangzhou → Hamburg", "Rail: Chengdu → Warsaw", "Sea: Ningbo → Rotterdam"],
    highlight: false,
  },
];

const containerTypes = [
  {
    type: "20GP LCL",
    icon: "ri-box-3-line",
    desc: "Less-than-container load consolidation. Ideal for pilot orders, mixed SKU shipments, or smaller B2B buyers testing our quality.",
    badge: "From 1 Pallet",
  },
  {
    type: "20GP FCL",
    icon: "ri-ship-line",
    desc: "Full 20-foot container. Standard for mid-volume B2B orders. Approx. 25–28 tons payload capacity for engine blocks.",
    badge: "~25 Tons",
  },
  {
    type: "40HQ FCL",
    icon: "ri-truck-line",
    desc: "High-cube 40-foot container for maximum volume efficiency. Preferred by distributors and large-scale importers.",
    badge: "~26 Tons / 76 CBM",
  },
];

const logistics = [
  { icon: "ri-file-text-line", label: "Full Export Documentation", desc: "CO, packing list, commercial invoice, B/L" },
  { icon: "ri-shield-check-line", label: "EAC & ISO Certified", desc: "Compliant with EAEU technical regulations" },
  { icon: "ri-time-line", label: "24h Quote Response", desc: "Dedicated B2B account management" },
  { icon: "ri-global-line", label: "15+ Active Export Markets", desc: "Proven track record across 3 continents" },
  { icon: "ri-map-pin-2-line", label: "Dual Port Access", desc: "Guangzhou & Ningbo for flexible routing" },
  { icon: "ri-train-line", label: "Rail Express Option", desc: "China-Europe Railway for inland delivery" },
];

export default function FMSupplyChain() {
  return (
    <section
      id="fm-supply"
      className="py-24 px-6"
      style={{ background: "#ffffff" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)" }}
          >
            <i className="ri-global-line text-xs" style={{ color: "#D97706" }} />
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: "#D97706", letterSpacing: "0.2em" }}
            >
              Global Supply Chain
            </span>
          </div>
          <h2
            className="font-black mb-4"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", color: "#1a1a1a", letterSpacing: "-0.01em" }}
          >
            Seamless Delivery to
            <br />
            <span style={{ color: "#D97706" }}>Your Market</span>
          </h2>
          <p
            className="text-base font-light max-w-2xl mx-auto"
            style={{ color: "rgba(30,20,5,0.55)", lineHeight: "1.85" }}
          >
            We handle the full logistics chain — from factory floor to your warehouse.
            Multimodal transport options from 20GP LCL to 40HQ FCL, with proven routes
            to Russia, the Middle East, and European markets.
          </p>
        </div>

        {/* Container types */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          {containerTypes.map((ct) => (
            <div
              key={ct.type}
              className="rounded-2xl p-7 transition-all duration-300 cursor-default"
              style={{
                background: "rgba(255,248,235,0.8)",
                border: "1px solid rgba(217,119,6,0.18)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(217,119,6,0.45)";
                (e.currentTarget as HTMLDivElement).style.background = "rgba(255,243,220,0.9)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(217,119,6,0.18)";
                (e.currentTarget as HTMLDivElement).style.background = "rgba(255,248,235,0.8)";
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl"
                  style={{ background: "rgba(217,119,6,0.1)" }}
                >
                  <i className={`${ct.icon} text-xl`} style={{ color: "#D97706" }} />
                </div>
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: "rgba(217,119,6,0.12)", color: "#D97706", border: "1px solid rgba(217,119,6,0.25)" }}
                >
                  {ct.badge}
                </span>
              </div>
              <h3
                className="font-black text-lg mb-3"
                style={{ color: "#1a1a1a" }}
              >
                {ct.type}
              </h3>
              <p
                className="text-sm font-light leading-relaxed"
                style={{ color: "rgba(30,20,5,0.6)", lineHeight: "1.8" }}
              >
                {ct.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Market destinations */}
        <div className="mb-16">
          <h3
            className="text-center font-black text-xl mb-8"
            style={{ color: "#1a1a1a" }}
          >
            Key Export Destinations
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {markets.map((market) => (
              <div
                key={market.region}
                className="rounded-2xl p-7"
                style={{
                  background: market.highlight ? "rgba(217,119,6,0.06)" : "rgba(250,245,235,0.7)",
                  border: market.highlight ? "1.5px solid rgba(217,119,6,0.35)" : "1px solid rgba(217,119,6,0.15)",
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span style={{ fontSize: "2rem", lineHeight: 1 }}>{market.flag}</span>
                  <h4
                    className="font-black text-base"
                    style={{ color: "#1a1a1a" }}
                  >
                    {market.region}
                  </h4>
                  {market.highlight && (
                    <span
                      className="ml-auto px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: "rgba(217,119,6,0.15)", color: "#D97706", fontSize: "9px", letterSpacing: "0.1em" }}
                    >
                      PRIMARY
                    </span>
                  )}
                </div>
                <p
                  className="text-sm font-light leading-relaxed mb-5"
                  style={{ color: "rgba(30,20,5,0.6)", lineHeight: "1.8" }}
                >
                  {market.desc}
                </p>
                <div className="flex flex-col gap-2">
                  {market.routes.map((route) => (
                    <div key={route} className="flex items-center gap-2">
                      <i className="ri-arrow-right-line text-xs" style={{ color: "#D97706" }} />
                      <span
                        className="text-xs font-medium"
                        style={{ color: "rgba(30,20,5,0.55)" }}
                      >
                        {route}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logistics guarantees grid */}
        <div
          className="rounded-2xl p-8"
          style={{ background: "rgba(255,248,235,0.6)", border: "1px solid rgba(217,119,6,0.15)" }}
        >
          <p
            className="text-xs font-semibold tracking-widest uppercase text-center mb-8"
            style={{ color: "rgba(30,20,5,0.4)", letterSpacing: "0.2em" }}
          >
            Logistics &amp; Compliance Guarantees
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {logistics.map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                  style={{ background: "rgba(217,119,6,0.1)" }}
                >
                  <i className={`${item.icon} text-base`} style={{ color: "#D97706" }} />
                </div>
                <div>
                  <p className="text-sm font-bold mb-0.5" style={{ color: "#1a1a1a" }}>
                    {item.label}
                  </p>
                  <p className="text-xs font-light" style={{ color: "rgba(30,20,5,0.5)" }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
